// external
import React, { FC, useEffect, useState } from 'react';
import { Typography, Button, TextField, Box, IconButton, Menu, MenuItem, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatDistanceToNow } from 'date-fns';

// internal
import useService from '../../../hooks/service.hook';
import EquipmentService from '../../../services/equipments.service';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import { palette } from '../../../theme';

export interface EquipmentNote {
  id: number;
  creator: string;
  equipment_id: number;
  message: string;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
}

interface EquipmentNotesTabProps {
  equipmentId?: number;
}

const EquipmentNotesTab: FC<EquipmentNotesTabProps> = ({ equipmentId }) => {
  const equipmentService = useService(EquipmentService);
  const { showSnackbar } = useSnackbarHelper();
  const [notes, setNotes] = useState<EquipmentNote[]>([]);
  const [newNoteText, setNewNoteText] = useState<string>('');
  const [noteMenuAnchorEl, setNoteMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [noteMenuId, setNoteMenuId] = useState<number | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteMessage, setEditingNoteMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (equipmentId) loadNotes();
    else setNotes([]);
  }, [equipmentId]);

  const loadNotes = async () => {
    if (!equipmentId) return;
    setLoading(true);
    try {
      const res = await equipmentService.getEquipmentNotes(equipmentId);
      setNotes(res.data.data || []);
    } catch (error) {
      console.error('Failed to load notes:', error);
      showSnackbar('Failed to load notes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleNoteMenuOpen = (event: React.MouseEvent<HTMLElement>, noteId: number) => {
    event.stopPropagation();
    setNoteMenuAnchorEl(event.currentTarget);
    setNoteMenuId(noteId);
  };
  const handleNoteMenuClose = () => {
    setNoteMenuAnchorEl(null);
    setNoteMenuId(null);
  };

  const handleEditNote = (noteId: number) => {
    handleNoteMenuClose();
    const note = notes.find((n) => n.id === noteId);
    if (!note) return;
    setTimeout(() => {
      setEditingNoteId(noteId);
      setEditingNoteMessage(note.message);
    }, 0);
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!equipmentId) return;
    handleNoteMenuClose();
    try {
      await equipmentService.deleteEquipmentNote(equipmentId, noteId);
      setNotes((prevNotes) => prevNotes.filter((n) => n.id !== noteId));
      showSnackbar('Note deleted', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || 'Failed to delete note. Please try again.', 'error');
    }
  };

  const handleCancelNoteEdit = () => {
    setEditingNoteId(null);
    setEditingNoteMessage('');
  };

  const handleSaveNote = async (noteId: number) => {
    if (!equipmentId) return;
    const trimmedMessage = editingNoteMessage.trim();
    const note = notes.find((n) => n.id === noteId);
    if (!note) return;
    if (trimmedMessage === note.message.trim()) return handleCancelNoteEdit();
    try {
      const res = await equipmentService.updateEquipmentNote(equipmentId, noteId, trimmedMessage);
      setNotes((prevNotes) =>
        prevNotes.map((n) =>
          n.id === noteId
            ? { ...n, message: trimmedMessage, updated_at: res.data.data?.updated_at || new Date().toISOString() }
            : n
        )
      );
      showSnackbar('Note updated', 'success');
      handleCancelNoteEdit();
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || 'Failed to update note. Please try again.', 'error');
    }
  };

  const handleNoteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      (e.target as HTMLElement).blur();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelNoteEdit();
    }
  };

  const handlePostNote = async () => {
    if (!equipmentId || !newNoteText.trim()) return;
    try {
      const res = await equipmentService.createEquipmentNote(equipmentId, newNoteText.trim());
      setNotes((prevNotes) => [res.data.data, ...prevNotes]);
      setNewNoteText('');
      showSnackbar('Note posted', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || 'Failed to post note. Please try again.', 'error');
    }
  };

  if (!equipmentId) {
    return (
      <Box sx={{ padding: 2, minHeight: 'fit-content', position: 'relative' }}>
        <Typography variant="body1" color={'text.secondary'}>
          Please save the equipment first to add notes.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: 'fit-content', position: 'relative' }}>
      {/* Refresh button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <IconButton
          onClick={loadNotes}
          disabled={loading}
          size="small"
          sx={{
            width: 28,
            height: 28,
            bgcolor: palette.grey[100],
            borderRadius: '6px',
            '&:hover': { bgcolor: palette.grey[200] },
          }}
          aria-label="Refresh notes"
        >
          <RefreshIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {/* Notes list */}
      <Box sx={{ maxHeight: 360, overflowY: 'auto', mb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : notes.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: palette.text.secondary, mb: 2 }}>
            No notes yet. Be the first to add a note!
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {[...notes]
              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
              .map((note) => (
                <Box
                  key={note.id}
                  sx={{
                    p: 1.5,
                    bgcolor: palette.background.subtle,
                    borderRadius: '10px',
                    border: `1px solid ${palette.divider}`,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: palette.primary.main }}>
                      {note.creator}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontSize: 11, color: palette.text.secondary }}>
                        {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                        {note.updated_at !== note.created_at ? ' (edited)' : ''}
                      </Typography>
                      {note.can_edit && (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => handleNoteMenuOpen(e, note.id)}
                            sx={{ p: '2px', width: 22, height: 22, '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}
                          >
                            <MoreVertIcon sx={{ fontSize: 14, color: palette.text.secondary }} />
                          </IconButton>
                          <Menu
                            anchorEl={noteMenuAnchorEl}
                            open={Boolean(noteMenuAnchorEl) && noteMenuId === note.id}
                            onClose={handleNoteMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            <MenuItem onClick={() => handleEditNote(note.id)} sx={{ fontSize: 13 }}>
                              <EditIcon sx={{ fontSize: 15, mr: 1, color: palette.text.secondary }} />
                              Edit
                            </MenuItem>
                            <MenuItem
                              onClick={() => handleDeleteNote(note.id)}
                              sx={{ fontSize: 13, color: 'error.main' }}
                            >
                              <DeleteIcon sx={{ fontSize: 15, mr: 1 }} />
                              Delete
                            </MenuItem>
                          </Menu>
                        </>
                      )}
                    </Box>
                  </Box>
                  {editingNoteId === note.id ? (
                    <TextField
                      multiline
                      fullWidth
                      value={editingNoteMessage}
                      onChange={(e) => setEditingNoteMessage(e.target.value)}
                      onBlur={() => handleSaveNote(note.id)}
                      onKeyDown={(e) => handleNoteKeyDown(e)}
                      autoFocus
                      variant="outlined"
                      size="small"
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: 13,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: palette.text.primary,
                      }}
                    >
                      {note.message}
                    </Typography>
                  )}
                </Box>
              ))}
          </Box>
        )}
      </Box>

      {/* Compose area */}
      <Box sx={{ borderTop: `1px solid ${palette.divider}`, pt: 1.5 }}>
        <TextField
          multiline
          rows={3}
          fullWidth
          value={newNoteText}
          onChange={(e) => setNewNoteText(e.target.value)}
          placeholder="Write your note here..."
          variant="outlined"
          size="small"
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13 },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePostNote}
            disabled={!newNoteText.trim()}
            sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderRadius: '8px', px: 2 }}
          >
            Post Note
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default EquipmentNotesTab;
